<template>
  <div class="map-container">
    <div :id="mapId" class="map-content"></div>
    <drag-box ref="dragBox"></drag-box>
    <frame-data-modal ref="frameDataModal"></frame-data-modal>
  </div>
</template>

<script>
import 'ol/ol.css';
import Map from 'ol/Map';
import View from 'ol/View';
import XYZ from 'ol/source/XYZ';
import * as olProj from 'ol/proj';
import { getUUID } from '@/utils/util';
import TileLayer from 'ol/layer/Tile';
import ImageLayer from 'ol/layer/Image';
import ImageWMS from 'ol/source/ImageWMS';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import Feature from 'ol/Feature';
import Point from 'ol/geom/Point';
import LineString from 'ol/geom/LineString';
import Polygon from 'ol/geom/Polygon';
import { Style, Circle, Stroke, Fill, Icon, Text } from 'ol/style';
import storage from 'store';
import { LonLat } from '@/store/mutation-types';
import { unByKey } from 'ol/Observable'
import ScaleLine from 'ol/control/ScaleLine'
import { defaults as defaultInteractions } from 'ol/interaction'
import { Translate } from 'ol/interaction'

import { calculateDistance, calculateTotalDistance, formatDistance, calculatePolygonArea, formatArea } from '@/utils/geoUtils'


import dragBox from '../dragBox'
import FrameDataModal from '../NewFrameDataModal'
export default {
  name: 'GisMap',
  components: {
    dragBox,
    FrameDataModal
  },
  data () {
    return {
      mapId: getUUID(), // 地图id
      map: null, // 地图实例
      baseLayer: null, // 底图图层
      selectedItemId: null, // 选中的图层ID
      clickTypeList: [], // 点击事件列表
      vectorSource: null, // 矢量数据源
      vectorLayer: null, // 矢量图层
      toolList: {
        measure: [{ name: '测距', type: 'measure-distance', selectable: true }, { name: '测面', type: 'measure-surface', selectable: true }],
        analyze: [{ name: '多边查询', type: 'analyze-query', selectable: true }, { name: '多边删除', type: 'analyze-delete', selectable: true }],
        export: [
          { name: '导出图片', type: 'export-image', selectable: false },
          { name: '框选打印', type: 'export-print', selectable: false },
          { name: '打印', type: 'print', selectable: false },
          { name: '导出DXF', type: 'export-dxf', selectable: false }
        ]
      },
      deleteUrl: '/pipe-network/gis/pipe-networks/region/statistics/delete',
      currentToolList: [], // 当前显示的工具列表
      showToolContainer: false, // 是否显示工具容器
      activeToolType: null, // 当前激活的工具类型
      // 测距相关属性
      measureDistancePoints: [], // 测距点集合
      measureDistanceSource: null, // 测距矢量数据源
      measureDistanceLayer: null, // 测距图层
      measureDistanceOverlay: null, // 测距标注覆盖层
      measureDistanceClickHandler: null, // 测距点击事件处理器
      measureDistanceTotalLabel: null, // 总距离标签
      // 测面相关属性
      measureSurfacePoints: [], // 测面点集合
      measureSurfaceSource: null, // 测面矢量数据源
      measureSurfaceLayer: null, // 测面图层
      measureSurfacePolygon: null, // 测面多边形
      measureSurfaceLabel: null, // 测面面积标签
      // 多边查询相关属性
      queryPolygonPoints: [], // 查询多边形点集合
      queryPolygonSource: null, // 查询多边形矢量数据源
      queryPolygonLayer: null, // 查询多边形图层
      queryPolygon: null, // 查询多边形
      queryPolygonHook: null, // 查询多边形钩子图标
      queryPolygonTranslate: null, // 查询多边形拖拽交互
      queryPolygonMidPoints: [], // 查询多边形中点集合
      // 多边删除相关属性
      deletePolygonPoints: [], // 删除多边形点集合
      deletePolygonSource: null, // 删除多边形矢量数据源
      deletePolygonLayer: null, // 删除多边形图层
      deletePolygon: null, // 删除多边形
      deletePolygonHook: null, // 删除多边形钩子图标
      deletePolygonTranslate: null, // 删除多边形拖拽交互
      deletePolygonMidPoints: [], // 删除多边形中点集合
      // 地图点击事件监听器
      mapClickListener: null,
      scaleLineControl: null, // 比例尺控件
      dxfExporting: false, // DXF导出状态
      highlightAnimationId: null, // 高亮动画ID
      // 独立的定位高亮图层（不会被 clearVectorLayer 清除）
      locationHighlightLayer: null,
      locationHighlightSource: null,
      // 点击要素高亮图层（点击地图上的点产生的红点，不会被 clearVectorLayer 清除）
      clickHighlightLayer: null,
      clickHighlightSource: null
    };
  },
  watch: {
    activeToolType (newType, oldType) {
      // 当工具类型变化时，清除之前工具的数据
      if (oldType === 'measure-distance') {
        this.clearMeasureDistance();
      }
      if (oldType === 'measure-surface') {
        this.clearMeasureSurface();
      }
      if (oldType === 'analyze-query') {
        this.clearQueryPolygon();
      }
      if (oldType === 'analyze-delete') {
        this.clearDeletePolygon();
      }
    },
    currentToolList (newList, oldList) {
      // 当工具列表变化时，重置激活的工具类型
      if (newList !== oldList) {
        this.activeToolType = null;
        this.clearMapCursorClass();
      }
    }
  },
  mounted () {
    console.log('BigMap组件已挂载，地图ID:', this.mapId);
    console.log('DOM元素:', document.getElementById(this.mapId));

    this.$nextTick(() => {
      console.log('DOM更新后，开始初始化地图...');
      this.initMap(); // 初始化地图
    });
  },
  beforeDestroy () {
    console.log('开始销毁地图组件，清理资源...');

    // 1. 清除所有工具状态
    this.activeToolType = null;
    this.clearMapCursorClass();

    // 2. 清除所有测量和分析工具
    this.clearMeasureDistance();
    this.clearMeasureSurface();
    this.clearQueryPolygon();
    this.clearDeletePolygon();
    this.clearSearchMarker();
    this.clearVectorLayer();

    // 3. 移除地图点击事件监听器
    if (this.mapClickListener) {
      unByKey(this.mapClickListener);
      this.mapClickListener = null;
    }

    // 4. 清除所有 WMS 图层的点击事件
    if (this.clickTypeList && this.clickTypeList.length > 0) {
      Object.keys(this.clickTypeList).forEach(key => {
        if (this.clickTypeList[key]) {
          unByKey(this.clickTypeList[key]);
          this.clickTypeList[key] = null;
        }
      });
      this.clickTypeList = [];
    }

    // 5. 移除所有图层
    if (this.map) {
      const layers = this.map.getLayers().getArray().slice(); // 复制数组避免迭代时修改
      layers.forEach(layer => {
        if (layer) {
          this.map.removeLayer(layer);
          // 清理图层资源
          if (layer.getSource()) {
            const source = layer.getSource();
            if (source.clear && typeof source.clear === 'function') {
              source.clear();
            }
            if (source.dispose && typeof source.dispose === 'function') {
              source.dispose();
            }
          }
          if (layer.dispose && typeof layer.dispose === 'function') {
            layer.dispose();
          }
        }
      });
    }

    // 6. 移除地图控件
    if (this.scaleLineControl && this.map) {
      this.map.removeControl(this.scaleLineControl);
      this.scaleLineControl = null;
    }

    // 7. 清空所有图层引用
    this.baseLayer = null;
    this.vectorLayer = null;
    this.vectorSource = null;
    this.searchMarkerLayer = null;
    this.searchMarkerSource = null;
    this.measureDistanceLayer = null;
    this.measureDistanceSource = null;
    this.measureSurfaceLayer = null;
    this.measureSurfaceSource = null;
    this.queryPolygonLayer = null;
    this.queryPolygonSource = null;
    this.deletePolygonLayer = null;
    this.deletePolygonSource = null;
    this.clickHighlightLayer = null;
    this.clickHighlightSource = null;

    // 8. 销毁地图实例
    if (this.map) {
      // 移除所有交互
      const interactions = this.map.getInteractions().getArray().slice();
      interactions.forEach(interaction => {
        this.map.removeInteraction(interaction);
      });

      // 移除所有叠加层
      const overlays = this.map.getOverlays().getArray().slice();
      overlays.forEach(overlay => {
        this.map.removeOverlay(overlay);
      });

      this.map.setTarget(null);
      this.map.dispose();
      this.map = null;
    }

    console.log('地图组件资源清理完成');
  },
  methods: {
    // 处理图层选择事件
    handleLayerSelected (data) {
      console.log('图层选择事件:', data)

      // 清除矢量图层
      this.clearVectorLayer();

      if (data.selected) {
        this.getWmsLayers(data.item);
      } else {
        this.removeWmsLayers(data.item);
      }
    },

    // 处理 selectedID 变化事件
    handleSelectedIdChange (data) {
      console.log('GisMap 接收到 selectedID 变化:', data)

      // 清除矢量图层
      this.clearVectorLayer();

      if (data.item) {
        this.selectedItemId = data.item.id;
      } else {
        this.selectedItemId = null;
      }
    },

    // 处理底图变化事件
    handleLayerChange (data) {
      console.log('GisMap 接收到底图变化:', data)
      // 先清除之前的底图
      this.clearBaseLayer();
      // 创建新的底图，传递完整的配置对象
      this.createBaseLayer(data);
    },

    // 处理关闭覆盖层事件
    handleCloseOverlay () {
      console.log('GisMap 接收到关闭覆盖层事件')
      // 清除矢量图层
      this.clearVectorLayer();
    },

    // 处理定位事件
    handlePositioning () {
      console.log('GisMap 接收到定位事件')
      // 清除矢量图层
      this.map.getView().setCenter(storage.get(LonLat));
    },

    // 处理查询事件
    handleQuerySearch (queryParams) {
      this.clearVectorLayer();
      console.log('GisMap 接收到查询事件:', queryParams)
      // 清除矢量图层
      this.getWmsLayers(queryParams);
    },

    // 清除图层
    clearLayerById (layerId) {
      console.log('GisMap 接收到清除图层事件:', layerId)
      this.clearVectorLayer();
      // 清除矢量图层
      this.removeWmsLayers({ id: layerId });
    },

    // 处理搜索结果事件
    handleResultSelected (result) {
      console.log('GisMap 接收到搜索结果事件:', result)

      // 拆分经纬度字符串
      const coords = result.lonlat.split(',');
      const longitude = parseFloat(coords[0]);
      const latitude = parseFloat(coords[1]);

      console.log('拆分后的经纬度:', { longitude, latitude });
      this.selectedPoint(longitude, latitude);
    },

    // 处理清除搜索事件
    handleClearSearch () {
      console.log('GisMap 接收到清除搜索事件');
      this.clearSearchMarker();
    },

    // 处理工具点击事件
    handleToolClick (key) {
      console.log('GisMap 接收到工具点击事件:', key);

      // 根据key获取对应的工具列表
      if (this.toolList[key]) {
        this.currentToolList = this.toolList[key];
        this.showToolContainer = true;
        console.log('显示工具列表:', this.currentToolList);
      } else {
        console.warn('未找到对应的工具列表:', key);
        this.showToolContainer = false;
      }
    },

    // 处理删除工具按钮点击
    handleDeleteTool () {
      console.log('关闭工具栏');
      this.showToolContainer = false;
      this.currentToolList = [];
      this.activeToolType = null; // 清除选中状态
      this.clearMapCursorClass(); // 清除光标类名
    },

    // 添加地图光标类名
    addMapCursorClass (toolType) {
      const mapElement = document.getElementById(this.mapId);
      if (mapElement) {
        mapElement.classList.add(`cursor-${toolType}`);
      }
    },

    // 清除地图光标类名
    clearMapCursorClass () {
      const mapElement = document.getElementById(this.mapId);
      if (mapElement) {
        // 移除所有光标相关的类名
        const classes = mapElement.className.split(' ').filter(cls => !cls.startsWith('cursor-'));
        mapElement.className = classes.join(' ');
      }
    },

    // 获取工具图标
    getToolIcon (type) {
      const iconMap = {
        'measure-distance': 'environment',
        'measure-surface': 'border',
        'analyze-query': 'search',
        'analyze-delete': 'delete',
        'export-image': 'camera',
        'export-print': 'printer',
        'print': 'printer',
        'export-dxf': 'download'
      };
      return iconMap[type] || 'tool';
    },

    // 处理工具项点击事件
    handleToolItemClick (item) {
      console.log('工具项点击:', item);

      // 只有可选择的工具才能切换选中状态
      if (item.selectable) {
        // 清除之前的光标类名
        this.clearMapCursorClass();

        // 切换选中状态
        if (this.activeToolType === item.type) {
          // 如果当前工具已选中，则取消选中
          this.activeToolType = null;
          // 如果是测距工具，结束测距
          if (item.type === 'measure-distance') {
            this.finishMeasureDistance();
          }
          // 如果是测面工具，结束测面
          if (item.type === 'measure-surface') {
            this.finishMeasureSurface();
          }
        } else {
          // 否则选中当前工具
          this.activeToolType = item.type;
          // 添加对应的光标类名
          this.addMapCursorClass(item.type);
        }
      }

      // 根据工具类型执行相应操作
      switch (item.type) {
        case 'measure-distance':
          console.log('执行测距功能');
          break;
        case 'measure-surface':
          console.log('执行测面功能');
          // TODO: 实现测面功能
          break;
        case 'analyze-query':
          console.log('执行多边查询功能');
          this.startPolygonQuery();
          break;
        case 'analyze-delete':
          console.log('执行多边删除功能');
          this.startPolygonDelete();
          break;
        case 'export-image':
          console.log('执行导出图片功能');
          this.exportMap();
          break;
        case 'export-print':
          console.log('执行框选打印功能');
          this.$refs.dragBox.selectBoxPrint();
          break;
        case 'print':
          console.log('执行打印功能');
          this.printMap();
          break;
        case 'export-dxf':
          console.log('执行导出DXF功能');
          this.exportDXF();
          break;
        default:
          console.log('未知工具类型:', item.type);
      }
    },
    // 获取地图实例
    getBigMap () {
      return this.map
    },

    // 初始化比例尺控件
    initScaleLine () {
      if (!this.map) {
        console.warn('GisMap: 地图实例为空，无法创建比例尺控件');
        return;
      }

      // 移除旧的比例尺控件
      if (this.scaleLineControl) {
        this.map.removeControl(this.scaleLineControl);
        this.scaleLineControl = null;
      }

      try {
        // 创建新的比例尺控件
        this.scaleLineControl = new ScaleLine({
          // 设置度量单位为米
          units: 'metric',
          target: 'scalebar',
          className: 'ol-scale-line'
        });

        // 添加到地图
        this.map.addControl(this.scaleLineControl);
        console.log('GisMap: 比例尺控件创建成功');
      } catch (error) {
        console.error('GisMap: 创建比例尺控件失败', error);
      }
    },
    // 初始化地图
    initMap () {
      try {
        console.log('开始初始化地图...');
        // 检查DOM元素是否存在
        const targetElement = document.getElementById(this.mapId);
        if (!targetElement) {
          console.error('地图容器元素不存在，ID:', this.mapId);
          return;
        }
        console.log('地图容器元素找到:', targetElement);

        const center = this.getInitialCenter();
        console.log('地图中心点:', center);

        this.map = new Map({
          controls: [],
          interactions: defaultInteractions({
            doubleClickZoom: false // 禁用双击放大
          }),
          view: new View({
            center: center,
            projection: 'EPSG:4326',
            zoom: 14,
            maxZoom: 28,
            minZoom: 1,
            constrainResolution: true, // 限制分辨率为整数缩放级别，提升性能
            smoothResolutionConstraint: false // 禁用平滑分辨率约束，加快缩放
          }),
          target: this.mapId,
          layers: [],
          loadTilesWhileAnimating: true, // 动画时继续加载瓦片
          loadTilesWhileInteracting: true // 交互时继续加载瓦片
        });

        console.log('地图实例创建成功:', this.map);

        // 创建比例尺控件
        this.initScaleLine();

        this.createBaseLayer();
        this.createVectorLayer();
        this.createSearchMarkerLayer();
        this.initMeasureDistanceLayer();
        this.initMeasureSurfaceLayer();
        // 保存地图点击事件监听器，方便后续清理
        this.mapClickListener = this.map.on('click', this.clickMap);
        console.log('地图初始化完成');
      } catch (error) {
        console.error('地图初始化失败:', error);
      }
    },
    // 中心定位
    getInitialCenter () {
      if (this.geo) {
        try {
          return JSON.parse(this.geo).coordinates;
        } catch (e) {
          console.error('解析geo属性失败:', e);
        }
      }
      return storage.get(LonLat);
    },
    // 加载底图
    createBaseLayer (config = {}) {
      // 支持旧的调用方式：createBaseLayer(url, projection, maxZoom)
      let url, projection, maxZoom, tileUrlFunction;

      if (typeof config === 'string') {
        // 旧的调用方式：第一个参数是URL字符串
        url = config;
        projection = arguments[1] || 'GCJ-02';
        maxZoom = arguments[2] || 18;
      } else {
        // 新的调用方式：传入配置对象
        url = config.mapUrl;
        projection = config.projection || 'GCJ-02';
        maxZoom = config.maxZoom || 18;
        tileUrlFunction = config.tileUrlFunction;
      }

      if (!url && !tileUrlFunction) {
        //默认必应地图
        projection = 'GCJ-02';
        maxZoom = 18;
        tileUrlFunction = (coord) => {
          return this.getVETileUrl(coord[0], coord[1], coord[2]);
        };
      }

      console.log('创建底图，使用投影:', projection, 'maxZoom:', maxZoom, 'URL:', url, 'tileUrlFunction:', !!tileUrlFunction);

      // 根据坐标系类型选择投影配置
      let projectionConfig = {}

      if (projection === 'GCJ-02') {
        // 使用火星坐标系（GCJ-02）
        // 检查是否已经注册过GCJ-02投影
        let gcjMecator = olProj.get('GCJ-02');

        if (!gcjMecator) {
          // 如果没有注册过，则创建并注册
          const gcj02Extent = [-20037508.342789244, -20037508.342789244, 20037508.342789244, 20037508.342789244]
          gcjMecator = new olProj.Projection({ code: 'GCJ-02', extent: gcj02Extent, units: 'm' })
          olProj.addProjection(gcjMecator)
          olProj.addCoordinateTransforms('EPSG:4326', gcjMecator, window.projzh.ll2gmerc, window.projzh.gmerc2ll)
          olProj.addCoordinateTransforms('EPSG:3857', gcjMecator, window.projzh.smerc2gmerc, window.projzh.gmerc2smerc)
          console.log('GCJ-02投影已注册');
        } else {
          console.log('GCJ-02投影已存在，直接使用');
        }

        projectionConfig = {
          projection: gcjMecator
        }
      } else {
        // 使用标准Web墨卡托投影（EPSG:3857）
        console.log('使用标准EPSG:3857投影');
        projectionConfig = {
          projection: 'EPSG:3857'
        }
      }

      // 构建 XYZ 数据源配置
      const xyzConfig = {
        ...projectionConfig,
        crossOrigin: 'anonymous',
        cacheSize: 2048, // 大幅增加缓存大小，从256提升到2048
        maxZoom: maxZoom,
        wrapX: true, // 启用水平环绕
        transition: 0, // 禁用瓦片加载过渡动画，加快渲染
        interpolate: false // 禁用图像平滑处理，加快渲染
      }

      // 根据是否有 tileUrlFunction 来决定使用哪种方式
      if (tileUrlFunction) {
        // 使用自定义瓦片URL函数（必应地图）
        xyzConfig.tileUrlFunction = tileUrlFunction;
        console.log('使用自定义 tileUrlFunction 创建底图');
      } else {
        // 使用URL模板（其他地图）
        xyzConfig.url = url;
      }

      this.baseLayer = new TileLayer({
        properties: {
          name: '底图',
          title: '地图图层',
          isBaseLayer: true
        },
        preload: 1, // 预加载1级瓦片
        source: new XYZ({
          ...xyzConfig,
          tileSize: 256,
          tilePixelRatio: 1, // 使用标准像素比，不使用高清瓦片
          reprojectionErrorThreshold: 0.5 // 重投影误差阈值，降低精度要求以提升速度
        }),
        useInterimTilesOnError: false, // 瓦片加载错误时不使用临时瓦片
        renderMode: 'image' // 使用image模式渲染，比vector快
      });

      this.map.getLayers().insertAt(0, this.baseLayer);
      console.log('底图创建完成，最大缩放级别:', maxZoom);
    },
    // 地图点击事件
    clickMap (event) {
      // 检查是否点击了钩子图标或中点
      const features = this.map.getFeaturesAtPixel(event.pixel);
      if (features.length > 0) {
        const feature = features[0];

        // 如果点击的是定位标记点，不做任何处理（只显示，不触发事件）
        if (feature.get('type') === 'location-marker') {
          return;
        }

        if (feature.get('type') === 'query-polygon-hook') {
          const coordinates = feature.get('coordinates');
          this.handleQueryPolygonHookClick(coordinates);
          return;
        }
        if (feature.get('type') === 'delete-polygon-hook') {
          const coordinates = feature.get('coordinates');
          this.handleDeletePolygonHookClick(coordinates);
          return;
        }
        // 处理查询多边形中点点击
        if (feature.get('type') === 'query-polygon-midpoint' && this.activeToolType === 'analyze-query') {
          this.handleQueryPolygonMidPointClick(feature);
          return;
        }
        // 处理删除多边形中点点击
        if (feature.get('type') === 'delete-polygon-midpoint' && this.activeToolType === 'analyze-delete') {
          this.handleDeletePolygonMidPointClick(feature);
          return;
        }
      }

      if (this.activeToolType == 'measure-distance') {
        this.measureDistance(event.coordinate);
      } else if (this.activeToolType == 'measure-surface') {
        this.measureSurface(event.coordinate);
      } else if (this.activeToolType == 'analyze-query') {
        this.addQueryPolygonPoint(event.coordinate);
      } else if (this.activeToolType == 'analyze-delete') {
        this.addDeletePolygonPoint(event.coordinate);
      }
    },

    // 处理查询多边形中点点击
    handleQueryPolygonMidPointClick (feature) {
      const edgeIndex = feature.get('edgeIndex');
      const midPointCoords = feature.getGeometry().getCoordinates();

      // 创建新的点要素
      const newPoint = new Point(midPointCoords);
      const newPointFeature = new Feature({
        geometry: newPoint,
        type: 'query-polygon-point',
        index: this.queryPolygonPoints.length
      });

      // 在正确的位置插入新点
      const insertIndex = (edgeIndex + 1) % this.queryPolygonPoints.length;
      this.queryPolygonPoints.splice(insertIndex, 0, newPointFeature);
      this.queryPolygonSource.addFeature(newPointFeature);

      // 更新所有点的索引
      this.queryPolygonPoints.forEach((point, index) => {
        point.set('index', index);
      });

      // 更新几何图形
      const pointCoords = this.queryPolygonPoints.map(p => p.getGeometry().getCoordinates());

      // 更新线段
      const features = this.queryPolygonSource.getFeatures();
      features.forEach(f => {
        if (f.get('type') === 'query-polygon-line') {
          this.queryPolygonSource.removeFeature(f);
        }
      });

      for (let i = 0; i < this.queryPolygonPoints.length - 1; i++) {
        const lineString = new LineString([pointCoords[i], pointCoords[i + 1]]);
        const lineFeature = new Feature({
          geometry: lineString,
          type: 'query-polygon-line',
          index: i
        });
        this.queryPolygonSource.addFeature(lineFeature);
      }

      // 更新多边形
      if (this.queryPolygon) {
        const polygonCoordinates = [...pointCoords, pointCoords[0]];
        this.queryPolygon.getGeometry().setCoordinates([polygonCoordinates]);
      }

      // 更新中点
      this.addQueryPolygonMidPoints();

      // 更新钩子
      if (this.queryPolygonHook) {
        this.queryPolygonSource.removeFeature(this.queryPolygonHook);
        const polygonCoordinates = [...pointCoords, pointCoords[0]];
        this.addQueryPolygonHook(polygonCoordinates);
      }
    },
    // 测距
    measureDistance (coordinate) {
      // 添加测距点
      this.measureDistancePoints.push(coordinate);

      // 创建测距点要素
      const point = new Point(coordinate);
      const pointFeature = new Feature({
        geometry: point,
        type: 'measure-point',
        index: this.measureDistancePoints.length - 1
      });

      // 添加到测距图层
      this.measureDistanceSource.addFeature(pointFeature);


      // 如果有多个点，创建线段
      if (this.measureDistancePoints.length > 1) {
        const lineCoordinates = this.measureDistancePoints.slice(-2);
        const lineString = new LineString(lineCoordinates);
        const lineFeature = new Feature({
          geometry: lineString,
          type: 'measure-line',
          index: this.measureDistancePoints.length - 2
        });

        this.measureDistanceSource.addFeature(lineFeature);

        // 计算并显示距离
        const distance = calculateDistance(lineCoordinates[0], lineCoordinates[1]);
        this.showDistanceLabel(lineCoordinates[1], distance);

        // 移除之前的总距离标签
        if (this.measureDistanceTotalLabel) {
          this.measureDistanceSource.removeFeature(this.measureDistanceTotalLabel);
          this.measureDistanceTotalLabel = null;
        }

        // 计算并显示总距离
        const totalDistance = calculateTotalDistance(this.measureDistancePoints);
        this.showTotalDistanceLabel(coordinate, totalDistance);
      }

      // 不自动结束测距，用户可以继续添加点
    },
    // 测面
    measureSurface (coordinate) {
      // 添加测面点
      this.measureSurfacePoints.push(coordinate);

      // 创建测面点要素
      const point = new Point(coordinate);
      const pointFeature = new Feature({
        geometry: point,
        type: 'measure-surface-point',
        index: this.measureSurfacePoints.length - 1
      });

      // 添加到测面图层
      this.measureSurfaceSource.addFeature(pointFeature);

      // 如果有多个点，创建线段或多边形
      if (this.measureSurfacePoints.length > 1) {
        // 创建线段
        const lineCoordinates = this.measureSurfacePoints.slice(-2);
        const lineString = new LineString(lineCoordinates);
        const lineFeature = new Feature({
          geometry: lineString,
          type: 'measure-surface-line',
          index: this.measureSurfacePoints.length - 2
        });

        this.measureSurfaceSource.addFeature(lineFeature);

        // 如果有3个或更多点，创建多边形
        if (this.measureSurfacePoints.length >= 3) {
          this.updateSurfacePolygon();
        }
      }
    },

    // 更新测面多边形
    updateSurfacePolygon () {
      // 移除之前的多边形
      if (this.measureSurfacePolygon) {
        this.measureSurfaceSource.removeFeature(this.measureSurfacePolygon);
      }

      // 创建新的多边形（闭合多边形）
      const polygonCoordinates = [...this.measureSurfacePoints];
      if (polygonCoordinates.length > 2) {
        // 确保多边形闭合
        polygonCoordinates.push(polygonCoordinates[0]);

        const polygon = new Polygon([polygonCoordinates]);
        const polygonFeature = new Feature({
          geometry: polygon,
          type: 'measure-surface-polygon'
        });

        this.measureSurfaceSource.addFeature(polygonFeature);
        this.measureSurfacePolygon = polygonFeature;

        // 计算并显示面积
        const area = calculatePolygonArea(this.measureSurfacePoints);
        this.showSurfaceLabel(polygonCoordinates[0], area);
      }
    },

    // 显示面积标签
    showSurfaceLabel (coordinate, area) {
      // 移除旧的面积标签（如果存在）
      if (this.measureSurfaceLabel) {
        this.measureSurfaceSource.removeFeature(this.measureSurfaceLabel);
        this.measureSurfaceLabel = null;
      }

      const areaText = formatArea(area);

      // 创建面积标签要素
      const point = new Point(coordinate);
      const labelFeature = new Feature({
        geometry: point,
        type: 'measure-surface-label',
        text: areaText
      });

      this.measureSurfaceSource.addFeature(labelFeature);
      // 保存标签引用，方便后续移除
      this.measureSurfaceLabel = labelFeature;
    },

    // 计算多边形中心点
    calculatePolygonCenter (coordinates) {
      let x = 0, y = 0;
      const count = coordinates.length - 1; // 减去闭合点

      for (let i = 0; i < count; i++) {
        x += coordinates[i][0];
        y += coordinates[i][1];
      }

      return [x / count, y / count];
    },

    // 开始多边查询
    startPolygonQuery () {
      console.log('开始多边查询');
      this.clearQueryPolygon();
      this.initQueryPolygonLayer();
    },

    // 添加查询多边形点
    addQueryPolygonPoint (coordinate) {
      // 创建查询多边形点要素
      const point = new Point(coordinate);
      const pointFeature = new Feature({
        geometry: point,
        type: 'query-polygon-point',
        index: this.queryPolygonPoints.length
      });

      // 将 Feature 对象添加到数组（而不是坐标）
      this.queryPolygonPoints.push(pointFeature);

      // 添加到查询多边形图层
      this.queryPolygonSource.addFeature(pointFeature);

      // 如果有多个点，创建线段或多边形
      if (this.queryPolygonPoints.length > 1) {
        // 创建线段
        const lineCoordinates = this.queryPolygonPoints.slice(-2).map(p => p.getGeometry().getCoordinates());
        const lineString = new LineString(lineCoordinates);
        const lineFeature = new Feature({
          geometry: lineString,
          type: 'query-polygon-line',
          index: this.queryPolygonPoints.length - 2
        });

        this.queryPolygonSource.addFeature(lineFeature);

        // 如果有3个或更多点，创建多边形
        if (this.queryPolygonPoints.length >= 3) {
          this.updateQueryPolygon();
        }
      }
    },

    // 更新查询多边形
    updateQueryPolygon () {
      // 移除之前的多边形和钩子
      if (this.queryPolygon) {
        this.queryPolygonSource.removeFeature(this.queryPolygon);
      }
      if (this.queryPolygonHook) {
        this.queryPolygonSource.removeFeature(this.queryPolygonHook);
      }

      // 从 Feature 对象中获取坐标
      const pointCoords = this.queryPolygonPoints.map(p => p.getGeometry().getCoordinates());

      if (pointCoords.length > 2) {
        // 创建闭合的多边形坐标
        const polygonCoordinates = [...pointCoords, pointCoords[0]];

        const polygon = new Polygon([polygonCoordinates]);
        const polygonFeature = new Feature({
          geometry: polygon,
          type: 'query-polygon'
        });

        this.queryPolygonSource.addFeature(polygonFeature);
        this.queryPolygon = polygonFeature;

        // 添加钩子图标
        this.addQueryPolygonHook(polygonCoordinates);

        // 添加中点并启用拖拽
        this.addQueryPolygonMidPoints();
        if (this.queryPolygonTranslate) {
          this.queryPolygonTranslate.setActive(true);
        }
      }
    },

    // 添加查询多边形钩子图标
    addQueryPolygonHook (polygonCoordinates) {
      // 计算多边形的中心点
      const center = this.calculatePolygonCenter(polygonCoordinates);

      // 从 Feature 对象中提取坐标数组（去掉闭合点）
      const coords = this.queryPolygonPoints.map(p => p.getGeometry().getCoordinates());

      // 创建钩子图标要素
      const hookPoint = new Point(center);
      const hookFeature = new Feature({
        geometry: hookPoint,
        type: 'query-polygon-hook',
        coordinates: coords // 保存坐标数组
      });

      this.queryPolygonSource.addFeature(hookFeature);
      this.queryPolygonHook = hookFeature;
    },

    // 处理查询多边形钩子点击事件
    handleQueryPolygonHookClick (coordinates) {
      console.log('查询多边形钩子被点击，获取面的坐标:', coordinates);

      // 格式化坐标信息
      const coordinateInfo = {
        type: 'query-polygon',
        coordinates: coordinates,
        pointCount: coordinates.length,
        area: calculatePolygonArea(coordinates)
      };
      // 显示坐标信息
      this.showQueryPolygonInfo(coordinateInfo);
    },

    // 显示查询多边形信息
    showQueryPolygonInfo (info) {
      console.log(info);

      let coordinates = [...info.coordinates];
      // 确保多边形闭合，在最后加上第一个坐标点
      if (coordinates.length > 0 && coordinates[0] !== coordinates[coordinates.length - 1]) {
        coordinates.push(coordinates[0]);
      }
      this.$refs.frameDataModal.edit(coordinates);
    },

    // 清除查询多边形
    clearQueryPolygon () {
      this.queryPolygonPoints = [];
      this.queryPolygon = null;
      this.queryPolygonHook = null;

      // 清除中点
      this.clearQueryPolygonMidPoints();

      // 禁用并移除拖拽交互
      if (this.queryPolygonTranslate) {
        this.queryPolygonTranslate.setActive(false);
        if (this.map) {
          this.map.removeInteraction(this.queryPolygonTranslate);
        }
        this.queryPolygonTranslate = null;
      }

      if (this.queryPolygonSource) {
        this.queryPolygonSource.clear();
      }
      if (this.queryPolygonLayer && this.map) {
        this.map.removeLayer(this.queryPolygonLayer);
        this.queryPolygonLayer = null;
        this.queryPolygonSource = null;
      }
    },

    // 初始化查询多边形图层
    initQueryPolygonLayer () {
      this.queryPolygonSource = new VectorSource();
      this.queryPolygonLayer = new VectorLayer({
        source: this.queryPolygonSource,
        zIndex: 1004,
        properties: {
          layerType: 'query-polygon'
        },
        style: (feature) => {
          const type = feature.get('type');
          if (type === 'query-polygon-point') {
            return new Style({
              image: new Circle({
                radius: 6,
                fill: new Fill({
                  color: '#1890ff'
                }),
                stroke: new Stroke({
                  color: '#ffffff',
                  width: 2
                })
              })
            });
          } else if (type === 'query-polygon-line') {
            return new Style({
              stroke: new Stroke({
                color: '#1890ff',
                width: 3,
                lineDash: [5, 5]
              })
            });
          } else if (type === 'query-polygon') {
            return new Style({
              fill: new Fill({
                color: 'rgba(24, 144, 255, 0.2)'
              }),
              stroke: new Stroke({
                color: '#1890ff',
                width: 2
              })
            });
          } else if (type === 'query-polygon-hook') {
            return new Style({
              image: new Icon({
                src: 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(`
                  <svg width="24" height="24" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="12" cy="12" r="10" fill="#52c41a" stroke="#ffffff" stroke-width="2"/>
                    <path d="M8 12l2 2 4-4" stroke="#ffffff" stroke-width="2" fill="none" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                `),
                scale: 1.2,
                anchor: [0.5, 0.5],
                anchorXUnits: 'fraction',
                anchorYUnits: 'fraction'
              })
            });
          } else if (type === 'query-polygon-midpoint') {
            return new Style({
              image: new Circle({
                radius: 5,
                fill: new Fill({
                  color: 'rgba(24, 144, 255, 0.5)'
                }),
                stroke: new Stroke({
                  color: 'rgba(24, 144, 255, 0.8)',
                  width: 1
                })
              })
            });
          }
        }
      });
      this.map.addLayer(this.queryPolygonLayer);

      // 添加拖拽交互
      this.queryPolygonTranslate = new Translate({
        features: this.queryPolygonSource.getFeaturesCollection(),
        filter: (feature) => {
          return feature.get('type') === 'query-polygon-point';
        }
      });
      this.map.addInteraction(this.queryPolygonTranslate);
      this.queryPolygonTranslate.setActive(false);

      // 监听拖拽变化
      this.queryPolygonSource.on('changefeature', this.updateQueryPolygonGeometries);
    },

    // 计算线段中点
    calculateQueryMidPoint (start, end) {
      return [
        (start[0] + end[0]) / 2,
        (start[1] + end[1]) / 2
      ];
    },

    // 添加查询多边形的中点
    addQueryPolygonMidPoints () {
      // 清除旧的中点
      this.clearQueryPolygonMidPoints();

      if (this.queryPolygonPoints.length >= 2) {
        for (let i = 0; i < this.queryPolygonPoints.length; i++) {
          const start = this.queryPolygonPoints[i].getGeometry().getCoordinates();
          const end = this.queryPolygonPoints[(i + 1) % this.queryPolygonPoints.length].getGeometry().getCoordinates();
          const midPoint = this.calculateQueryMidPoint(start, end);

          const pointFeature = new Feature({
            geometry: new Point(midPoint),
            type: 'query-polygon-midpoint',
            edgeIndex: i
          });

          this.queryPolygonMidPoints.push(pointFeature);
          this.queryPolygonSource.addFeature(pointFeature);
        }
      }
    },

    // 清除查询多边形中点
    clearQueryPolygonMidPoints () {
      this.queryPolygonMidPoints.forEach(feature => {
        this.queryPolygonSource.removeFeature(feature);
      });
      this.queryPolygonMidPoints = [];
    },

    // 更新查询多边形几何图形（拖拽后）
    updateQueryPolygonGeometries (event) {
      const movedFeature = event.feature;
      if (movedFeature.get('type') !== 'query-polygon-point') return;

      // 获取所有点的坐标
      const pointCoords = this.queryPolygonPoints.map(point => point.getGeometry().getCoordinates());

      // 更新线段
      const features = this.queryPolygonSource.getFeatures();
      features.forEach(feature => {
        if (feature.get('type') === 'query-polygon-line') {
          this.queryPolygonSource.removeFeature(feature);
        }
      });

      // 重新创建线段
      for (let i = 0; i < this.queryPolygonPoints.length - 1; i++) {
        const lineString = new LineString([pointCoords[i], pointCoords[i + 1]]);
        const lineFeature = new Feature({
          geometry: lineString,
          type: 'query-polygon-line',
          index: i
        });
        this.queryPolygonSource.addFeature(lineFeature);
      }

      // 更新多边形
      if (this.queryPolygon) {
        const polygonCoordinates = [...pointCoords, pointCoords[0]];
        this.queryPolygon.getGeometry().setCoordinates([polygonCoordinates]);
      }

      // 更新中点
      this.addQueryPolygonMidPoints();

      // 更新钩子位置
      if (this.queryPolygonHook) {
        this.queryPolygonSource.removeFeature(this.queryPolygonHook);
        const polygonCoordinates = [...pointCoords, pointCoords[0]];
        this.addQueryPolygonHook(polygonCoordinates);
      }
    },

    // 开始多边删除
    startPolygonDelete () {
      console.log('开始多边删除');
      this.clearDeletePolygon();
      this.initDeletePolygonLayer();
    },

    // 添加删除多边形点
    addDeletePolygonPoint (coordinate) {
      // 创建删除多边形点要素
      const point = new Point(coordinate);
      const pointFeature = new Feature({
        geometry: point,
        type: 'delete-polygon-point',
        index: this.deletePolygonPoints.length
      });

      // 将 Feature 对象添加到数组（而不是坐标）
      this.deletePolygonPoints.push(pointFeature);

      // 添加到删除多边形图层
      this.deletePolygonSource.addFeature(pointFeature);

      // 如果有多个点，创建线段或多边形
      if (this.deletePolygonPoints.length > 1) {
        // 创建线段
        const lineCoordinates = this.deletePolygonPoints.slice(-2).map(p => p.getGeometry().getCoordinates());
        const lineString = new LineString(lineCoordinates);
        const lineFeature = new Feature({
          geometry: lineString,
          type: 'delete-polygon-line',
          index: this.deletePolygonPoints.length - 2
        });

        this.deletePolygonSource.addFeature(lineFeature);

        // 如果有3个或更多点，创建多边形
        if (this.deletePolygonPoints.length >= 3) {
          this.updateDeletePolygon();
        }
      }
    },

    // 更新删除多边形
    updateDeletePolygon () {
      // 移除之前的多边形和钩子
      if (this.deletePolygon) {
        this.deletePolygonSource.removeFeature(this.deletePolygon);
      }
      if (this.deletePolygonHook) {
        this.deletePolygonSource.removeFeature(this.deletePolygonHook);
      }

      // 从 Feature 对象中获取坐标
      const pointCoords = this.deletePolygonPoints.map(p => p.getGeometry().getCoordinates());

      if (pointCoords.length > 2) {
        // 创建闭合的多边形坐标
        const polygonCoordinates = [...pointCoords, pointCoords[0]];

        const polygon = new Polygon([polygonCoordinates]);
        const polygonFeature = new Feature({
          geometry: polygon,
          type: 'delete-polygon'
        });

        this.deletePolygonSource.addFeature(polygonFeature);
        this.deletePolygon = polygonFeature;

        // 添加钩子图标
        this.addDeletePolygonHook(polygonCoordinates);

        // 添加中点并启用拖拽
        this.addDeletePolygonMidPoints();
        if (this.deletePolygonTranslate) {
          this.deletePolygonTranslate.setActive(true);
        }
      }
    },

    // 添加删除多边形钩子图标
    addDeletePolygonHook (polygonCoordinates) {
      // 计算多边形的中心点
      const center = this.calculatePolygonCenter(polygonCoordinates);

      // 从 Feature 对象中提取坐标数组（去掉闭合点）
      const coords = this.deletePolygonPoints.map(p => p.getGeometry().getCoordinates());

      // 创建钩子图标要素
      const hookPoint = new Point(center);
      const hookFeature = new Feature({
        geometry: hookPoint,
        type: 'delete-polygon-hook',
        coordinates: coords // 保存坐标数组
      });

      this.deletePolygonSource.addFeature(hookFeature);
      this.deletePolygonHook = hookFeature;
    },

    // 处理删除多边形钩子点击事件
    handleDeletePolygonHookClick (coordinates) {
      console.log('删除多边形钩子被点击，获取面的坐标:', coordinates);

      // 格式化坐标信息
      const coordinateInfo = {
        type: 'delete-polygon',
        coordinates: coordinates,
        pointCount: coordinates.length,
        area: calculatePolygonArea(coordinates)
      };


      // 显示删除确认信息
      this.showDeletePolygonInfo(coordinateInfo);
    },

    // 显示删除多边形信息
    showDeletePolygonInfo (info) {
      let coordinates = [...info.coordinates];
      // 确保多边形闭合，在最后加上第一个坐标点
      if (coordinates.length > 0 && coordinates[0] !== coordinates[coordinates.length - 1]) {
        coordinates.push(coordinates[0]);
      }
      // 直接显示删除确认对话框
      this.deleteHandle(coordinates);
    },

    // 删除区域数据 - 直接显示确认对话框
    deleteHandle (coordinates) {
      const that = this
      this.$confirm({
        title: '确认删除',
        content: '确定要删除此区域内的管点,物探点信息？',
        okText: '确认',
        cancelText: '取消',
        okType: 'danger',
        onOk () {
          that.executeDelete(coordinates)
        }
      })
    },

    // 执行删除操作
    executeDelete (coordinates) {

      const params = {
        linestring: coordinates
      }

      this.$http
        .post(this.deleteUrl, params)
        .then(({ data }) => {
          this.$message.success('删除成功')
          // 刷新数据
          this.clearDeletePolygon();
          this.map.getView().setZoom(this.map.getView().getZoom() + 0.01);
        })
        .catch(() => {
          this.$message.error('删除失败，请重试')
        })
    },

    // 清除删除多边形
    clearDeletePolygon () {
      this.deletePolygonPoints = [];
      this.deletePolygon = null;
      this.deletePolygonHook = null;

      // 清除中点
      this.clearDeletePolygonMidPoints();

      // 禁用并移除拖拽交互
      if (this.deletePolygonTranslate) {
        this.deletePolygonTranslate.setActive(false);
        if (this.map) {
          this.map.removeInteraction(this.deletePolygonTranslate);
        }
        this.deletePolygonTranslate = null;
      }

      if (this.deletePolygonSource) {
        this.deletePolygonSource.clear();
      }
      if (this.deletePolygonLayer && this.map) {
        this.map.removeLayer(this.deletePolygonLayer);
        this.deletePolygonLayer = null;
        this.deletePolygonSource = null;
      }
    },

    // 初始化删除多边形图层
    initDeletePolygonLayer () {
      this.deletePolygonSource = new VectorSource();
      this.deletePolygonLayer = new VectorLayer({
        source: this.deletePolygonSource,
        zIndex: 1005,
        style: (feature) => {
          const type = feature.get('type');
          if (type === 'delete-polygon-point') {
            return new Style({
              image: new Circle({
                radius: 6,
                fill: new Fill({
                  color: '#ff4d4f'
                }),
                stroke: new Stroke({
                  color: '#ffffff',
                  width: 2
                })
              })
            });
          } else if (type === 'delete-polygon-line') {
            return new Style({
              stroke: new Stroke({
                color: '#ff4d4f',
                width: 3,
                lineDash: [5, 5]
              })
            });
          } else if (type === 'delete-polygon') {
            return new Style({
              fill: new Fill({
                color: 'rgba(255, 77, 79, 0.2)'
              }),
              stroke: new Stroke({
                color: '#ff4d4f',
                width: 2
              })
            });
          } else if (type === 'delete-polygon-hook') {
            return new Style({
              image: new Icon({
                src: 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(`
                  <svg width="24" height="24" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="12" cy="12" r="10" fill="#ff4d4f" stroke="#ffffff" stroke-width="2"/>
                    <path d="M8 8l8 8M16 8l-8 8" stroke="#ffffff" stroke-width="2" fill="none" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                `),
                scale: 1.2,
                anchor: [0.5, 0.5],
                anchorXUnits: 'fraction',
                anchorYUnits: 'fraction'
              })
            });
          } else if (type === 'delete-polygon-midpoint') {
            return new Style({
              image: new Circle({
                radius: 5,
                fill: new Fill({
                  color: 'rgba(255, 77, 79, 0.5)'
                }),
                stroke: new Stroke({
                  color: 'rgba(255, 77, 79, 0.8)',
                  width: 1
                })
              })
            });
          }
        }
      });
      this.map.addLayer(this.deletePolygonLayer);

      // 添加拖拽交互
      this.deletePolygonTranslate = new Translate({
        features: this.deletePolygonSource.getFeaturesCollection(),
        filter: (feature) => {
          return feature.get('type') === 'delete-polygon-point';
        }
      });
      this.map.addInteraction(this.deletePolygonTranslate);
      this.deletePolygonTranslate.setActive(false);

      // 监听拖拽变化
      this.deletePolygonSource.on('changefeature', this.updateDeletePolygonGeometries);
    },

    // 添加删除多边形的中点
    addDeletePolygonMidPoints () {
      // 清除旧的中点
      this.clearDeletePolygonMidPoints();

      if (this.deletePolygonPoints.length >= 2) {
        for (let i = 0; i < this.deletePolygonPoints.length; i++) {
          const start = this.deletePolygonPoints[i].getGeometry().getCoordinates();
          const end = this.deletePolygonPoints[(i + 1) % this.deletePolygonPoints.length].getGeometry().getCoordinates();
          const midPoint = this.calculateQueryMidPoint(start, end);

          const pointFeature = new Feature({
            geometry: new Point(midPoint),
            type: 'delete-polygon-midpoint',
            edgeIndex: i
          });

          this.deletePolygonMidPoints.push(pointFeature);
          this.deletePolygonSource.addFeature(pointFeature);
        }
      }
    },

    // 清除删除多边形中点
    clearDeletePolygonMidPoints () {
      this.deletePolygonMidPoints.forEach(feature => {
        this.deletePolygonSource.removeFeature(feature);
      });
      this.deletePolygonMidPoints = [];
    },

    // 更新删除多边形几何图形（拖拽后）
    updateDeletePolygonGeometries (event) {
      const movedFeature = event.feature;
      if (movedFeature.get('type') !== 'delete-polygon-point') return;

      // 获取所有点的坐标
      const pointCoords = this.deletePolygonPoints.map(point => point.getGeometry().getCoordinates());

      // 更新线段
      const features = this.deletePolygonSource.getFeatures();
      features.forEach(feature => {
        if (feature.get('type') === 'delete-polygon-line') {
          this.deletePolygonSource.removeFeature(feature);
        }
      });

      // 重新创建线段
      for (let i = 0; i < this.deletePolygonPoints.length - 1; i++) {
        const lineString = new LineString([pointCoords[i], pointCoords[i + 1]]);
        const lineFeature = new Feature({
          geometry: lineString,
          type: 'delete-polygon-line',
          index: i
        });
        this.deletePolygonSource.addFeature(lineFeature);
      }

      // 更新多边形
      if (this.deletePolygon) {
        const polygonCoordinates = [...pointCoords, pointCoords[0]];
        this.deletePolygon.getGeometry().setCoordinates([polygonCoordinates]);
      }

      // 更新中点
      this.addDeletePolygonMidPoints();

      // 更新钩子位置
      if (this.deletePolygonHook) {
        this.deletePolygonSource.removeFeature(this.deletePolygonHook);
        const polygonCoordinates = [...pointCoords, pointCoords[0]];
        this.addDeletePolygonHook(polygonCoordinates);
      }
    },

    // 处理删除多边形中点点击
    handleDeletePolygonMidPointClick (feature) {
      const edgeIndex = feature.get('edgeIndex');
      const midPointCoords = feature.getGeometry().getCoordinates();

      // 创建新的点要素
      const newPoint = new Point(midPointCoords);
      const newPointFeature = new Feature({
        geometry: newPoint,
        type: 'delete-polygon-point',
        index: this.deletePolygonPoints.length
      });

      // 在正确的位置插入新点
      const insertIndex = (edgeIndex + 1) % this.deletePolygonPoints.length;
      this.deletePolygonPoints.splice(insertIndex, 0, newPointFeature);
      this.deletePolygonSource.addFeature(newPointFeature);

      // 更新所有点的索引
      this.deletePolygonPoints.forEach((point, index) => {
        point.set('index', index);
      });

      // 更新几何图形
      const pointCoords = this.deletePolygonPoints.map(p => p.getGeometry().getCoordinates());

      // 更新线段
      const features = this.deletePolygonSource.getFeatures();
      features.forEach(f => {
        if (f.get('type') === 'delete-polygon-line') {
          this.deletePolygonSource.removeFeature(f);
        }
      });

      for (let i = 0; i < this.deletePolygonPoints.length - 1; i++) {
        const lineString = new LineString([pointCoords[i], pointCoords[i + 1]]);
        const lineFeature = new Feature({
          geometry: lineString,
          type: 'delete-polygon-line',
          index: i
        });
        this.deletePolygonSource.addFeature(lineFeature);
      }

      // 更新多边形
      if (this.deletePolygon) {
        const polygonCoordinates = [...pointCoords, pointCoords[0]];
        this.deletePolygon.getGeometry().setCoordinates([polygonCoordinates]);
      }

      // 更新中点
      this.addDeletePolygonMidPoints();

      // 更新钩子
      if (this.deletePolygonHook) {
        this.deletePolygonSource.removeFeature(this.deletePolygonHook);
        const polygonCoordinates = [...pointCoords, pointCoords[0]];
        this.addDeletePolygonHook(polygonCoordinates);
      }
    },

    // 完成测面
    finishMeasureSurface () {
      if (this.measureSurfacePoints.length > 0) {
        // 清除测面状态
        this.clearMeasureSurface();
      } else {
        // 如果没有测面点，直接清除状态
        this.clearMeasureSurface();
      }
    },

    // 清除测面
    clearMeasureSurface () {
      this.measureSurfacePoints = [];
      this.measureSurfacePolygon = null;
      this.measureSurfaceLabel = null; // 清除面积标签引用
      if (this.measureSurfaceSource) {
        this.measureSurfaceSource.clear();
      }
      if (this.measureSurfaceLayer && this.map) {
        this.map.removeLayer(this.measureSurfaceLayer);
        this.measureSurfaceLayer = null;
        this.measureSurfaceSource = null;
        // 重新初始化图层
        this.initMeasureSurfaceLayer();
      }
    },

    // 初始化测面图层
    initMeasureSurfaceLayer () {
      this.measureSurfaceSource = new VectorSource();
      this.measureSurfaceLayer = new VectorLayer({
        source: this.measureSurfaceSource,
        zIndex: 1003,
        style: (feature) => {
          const type = feature.get('type');
          if (type === 'measure-surface-point') {
            return new Style({
              image: new Circle({
                radius: 6,
                fill: new Fill({
                  color: '#52c41a'
                }),
                stroke: new Stroke({
                  color: '#ffffff',
                  width: 2
                })
              })
            });
          } else if (type === 'measure-surface-line') {
            return new Style({
              stroke: new Stroke({
                color: '#52c41a',
                width: 3,
                lineDash: [5, 5]
              })
            });
          } else if (type === 'measure-surface-polygon') {
            return new Style({
              fill: new Fill({
                color: 'rgba(82, 196, 26, 0.2)'
              }),
              stroke: new Stroke({
                color: '#52c41a',
                width: 2
              })
            });
          } else if (type === 'measure-surface-label') {
            return new Style({
              text: new Text({
                text: feature.get('text'),
                font: '14px Arial',
                fill: new Fill({
                  color: '#52c41a'
                }),
                stroke: new Stroke({
                  color: '#ffffff',
                  width: 2
                }),
                offsetY: -15
              })
            });
          }
        }
      });
      this.map.addLayer(this.measureSurfaceLayer);
    },


    // 显示距离标签
    showDistanceLabel (coordinate, distance) {
      const distanceText = formatDistance(distance);

      // 创建距离标签要素
      const point = new Point(coordinate);
      const labelFeature = new Feature({
        geometry: point,
        type: 'measure-label',
        text: distanceText
      });

      this.measureDistanceSource.addFeature(labelFeature);
    },

    // 显示总距离标签
    showTotalDistanceLabel (coordinate, totalDistance) {
      const totalDistanceText = '总距离: ' + formatDistance(totalDistance);

      // 创建总距离标签要素
      const point = new Point(coordinate);
      const labelFeature = new Feature({
        geometry: point,
        type: 'measure-total-label',
        text: totalDistanceText
      });

      this.measureDistanceSource.addFeature(labelFeature);
      this.measureDistanceTotalLabel = labelFeature;
    },

    // 完成测距
    finishMeasureDistance () {
      if (this.measureDistancePoints.length > 0) {

        this.clearMeasureDistance();

      } else {
        // 如果没有测距点，直接清除状态
        this.clearMeasureDistance();
      }
    },


    // 显示测距提示
    showMeasureTip (message) {
      // 移除之前的提示
      const existingTip = document.querySelector('.measure-distance-tip');
      if (existingTip) {
        existingTip.remove();
      }

      // 创建新提示
      const tip = document.createElement('div');
      tip.className = 'measure-distance-tip';
      tip.textContent = message;

      // 确保提示不会影响页面布局
      tip.style.position = 'fixed';
      tip.style.top = '50%';
      tip.style.left = '50%';
      tip.style.transform = 'translate(-50%, -50%)';
      tip.style.zIndex = '10000';
      tip.style.pointerEvents = 'none';

      document.body.appendChild(tip);


      if (tip.parentNode) {
        tip.parentNode.removeChild(tip);
      }

    },


    // 清除测距
    clearMeasureDistance () {
      this.measureDistancePoints = [];
      this.measureDistanceTotalLabel = null;
      if (this.measureDistanceSource) {
        this.measureDistanceSource.clear();
      }
      if (this.measureDistanceLayer && this.map) {
        this.map.removeLayer(this.measureDistanceLayer);
        this.measureDistanceLayer = null;
        this.measureDistanceSource = null;
        // 重新初始化图层
        this.initMeasureDistanceLayer();
      }
    },

    // 初始化测距图层
    initMeasureDistanceLayer () {
      this.measureDistanceSource = new VectorSource();
      this.measureDistanceLayer = new VectorLayer({
        source: this.measureDistanceSource,
        zIndex: 1002,
        style: (feature) => {
          const type = feature.get('type');
          if (type === 'measure-point') {
            return new Style({
              image: new Circle({
                radius: 6,
                fill: new Fill({
                  color: '#ff4757'
                }),
                stroke: new Stroke({
                  color: '#ffffff',
                  width: 2
                })
              })
            });
          } else if (type === 'measure-line') {
            return new Style({
              stroke: new Stroke({
                color: '#ff4757',
                width: 3,
                lineDash: [5, 5]
              })
            });
          } else if (type === 'measure-label') {
            return new Style({
              text: new Text({
                text: feature.get('text'),
                font: '14px Arial',
                fill: new Fill({
                  color: '#ff4757'
                }),
                stroke: new Stroke({
                  color: '#ffffff',
                  width: 2
                }),
                offsetY: -15
              })
            });
          } else if (type === 'measure-total-label') {
            return new Style({
              text: new Text({
                text: feature.get('text'),
                font: 'bold 14px Arial',
                fill: new Fill({
                  color: '#ff4757'
                }),
                stroke: new Stroke({
                  color: '#ffffff',
                  width: 3
                }),
                offsetY: 20,
              })
            });
          }
        }
      });
      this.map.addLayer(this.measureDistanceLayer);
    },
    // 创建矢量图层
    createVectorLayer () {
      this.vectorSource = new VectorSource();
      this.vectorLayer = new VectorLayer({
        source: this.vectorSource,
        zIndex: 1000, // 设置高层级
        style: new Style({
          fill: new Fill({
            color: 'rgba(255, 0, 0, 0.3)'
          }),
          stroke: new Stroke({
            color: '#ff0000',
            width: 3
          }),
          image: new Circle({
            radius: 8,
            fill: new Fill({
              color: '#ff0000'
            }),
            stroke: new Stroke({
              color: '#ffffff',
              width: 2
            })
          })
        })
      });
      this.map.addLayer(this.vectorLayer);

      // 创建点击高亮图层（用于存储点击地图产生的红点，不会被 clearVectorLayer 清除）
      this.createClickHighlightLayer();
    },

    // 创建点击高亮图层
    createClickHighlightLayer () {
      this.clickHighlightSource = new VectorSource();
      this.clickHighlightLayer = new VectorLayer({
        source: this.clickHighlightSource,
        zIndex: 2000, // 设置更高的层级，确保在最上层显示
        style: new Style({
          image: new Circle({
            radius: 10,
            fill: new Fill({
              color: '#ff0000' // 红色填充
            }),
            stroke: new Stroke({
              color: '#ffffff',
              width: 3
            })
          })
        })
      });
      this.map.addLayer(this.clickHighlightLayer);
    },

    // 清除点击高亮图层
    clearClickHighlight () {
      if (this.clickHighlightSource) {
        this.clickHighlightSource.clear();
        console.log('点击高亮图层已清除');
      }
    },

    // 创建搜索标记图层
    createSearchMarkerLayer () {
      this.searchMarkerSource = new VectorSource();
      this.searchMarkerLayer = new VectorLayer({
        source: this.searchMarkerSource,
        zIndex: 1001, // 比普通矢量图层层级更高
        style: new Style({
          image: new Icon({
            src: 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(`
              <svg width="32" height="48" viewBox="0 0 32 48" xmlns="http://www.w3.org/2000/svg">
                <path d="M16 2C8.27 2 2 8.27 2 16c0 6.5 4.5 11.5 10 14.5l4 2.5 4-2.5c5.5-3 10-8 10-14.5C30 8.27 23.73 2 16 2z" 
                      fill="#ff4757" stroke="#ffffff" stroke-width="2"/>
                <circle cx="16" cy="16" r="6" fill="#ffffff"/>
                <circle cx="16" cy="16" r="3" fill="#ff4757"/>
              </svg>
            `),
            scale: 1,
            anchor: [0.5, 1], // 锚点在水滴底部
            anchorXUnits: 'fraction',
            anchorYUnits: 'fraction'
          })
        })
      });
      this.map.addLayer(this.searchMarkerLayer);
    },

    // 清除矢量图层
    clearVectorLayer () {
      if (this.vectorSource) {
        this.vectorSource.clear();
        console.log('矢量图层已清除');
      }
      // 通知 InfoMap 隐藏详情面板
      this.$emit('clear-vector-layer');
    },

    // 清除定位高亮图层（独立方法，不影响其他矢量图层）
    clearLocationHighlight () {
      console.log('清除定位高亮图层');

      // 移除高亮图层
      if (this.locationHighlightLayer && this.map) {
        this.map.removeLayer(this.locationHighlightLayer);
        this.locationHighlightLayer = null;
      }

      // 清空数据源
      if (this.locationHighlightSource) {
        this.locationHighlightSource.clear();
        this.locationHighlightSource = null;
      }
    },

    // 清除点击高亮图层（独立方法）
    clearClickHighlightLayer () {
      console.log('清除点击高亮图层');

      // 移除点击高亮图层
      if (this.clickHighlightLayer && this.map) {
        this.map.removeLayer(this.clickHighlightLayer);
        this.clickHighlightLayer = null;
      }

      // 清空数据源
      if (this.clickHighlightSource) {
        this.clickHighlightSource.clear();
        this.clickHighlightSource = null;
      }
    },

    // 清除底图图层
    clearBaseLayer () {
      if (this.map && this.baseLayer) {
        // 清理底图数据源
        const source = this.baseLayer.getSource();
        if (source) {
          if (source.clear && typeof source.clear === 'function') {
            source.clear();
          }
          if (source.dispose && typeof source.dispose === 'function') {
            source.dispose();
          }
        }

        // 从地图中移除底图图层
        this.map.removeLayer(this.baseLayer);

        // 清理图层
        if (this.baseLayer.dispose && typeof this.baseLayer.dispose === 'function') {
          this.baseLayer.dispose();
        }

        this.baseLayer = null;
        console.log('底图图层已清除');
      }
    },

    // 必应地图瓦片URL生成器
    getVETileUrl (z, x, y) {
      let quadKey = '';
      let tileX = x;
      let tileY = y;

      for (let i = 0; i < z; i++) {
        quadKey = ((tileX & 1) + 2 * (tileY & 1)).toString() + quadKey;
        tileX >>= 1;
        tileY >>= 1;
      }

      return 'http://dynamic.t0.tiles.ditu.live.com/comp/ch/' + quadKey + '?it=G,VE,BX,L,LA&mkt=zh-cn,syr&n=z&og=111&ur=CN';
    },

    //GEOSERVER图层添加
    // 添加WMS图层
    getWmsLayers (item) {
      //获取请求参数
      const params = {
        LAYERS: item.dataSource,
        FORMAT: 'image/png8',
        CQL_FILTER: item.showFilterCondition || '',
      }
      //创建WMS源
      const wmsSource = new ImageWMS({
        crossOrigin: 'anonymous',
        params,
        serverType: 'geoserver',
        url: `/api/geoserver/${item.workspace}/wms`,
        hidpi: false,
      })
      //创建WMS图层
      const wmsLayer = new ImageLayer({
        id: item.id,
        name: item.name,
        crossOrigin: 'anonymous',
        projection: 'EPSG:4490',
        source: wmsSource,
        zIndex: item.layerShowSort || 99
      })
      //添加WMS图层
      this.map.addLayer(wmsLayer);
      //添加WMS点击事件
      this.clickWMS(wmsSource, item, wmsLayer);
    },
    // 移除WMS图层
    removeWmsLayers (item) {
      const layer = this.map.getLayers().getArray().find(layer => layer.values_.id === item.id);
      if (layer) {
        // 清理图层数据源
        const source = layer.getSource();
        if (source) {
          if (source.clear && typeof source.clear === 'function') {
            source.clear();
          }
          if (source.dispose && typeof source.dispose === 'function') {
            source.dispose();
          }
        }

        // 移除图层
        this.map.removeLayer(layer);

        // 清理图层
        if (layer.dispose && typeof layer.dispose === 'function') {
          layer.dispose();
        }
      }

      // 移除点击事件监听器
      if (this.clickTypeList[item.id]) {
        unByKey(this.clickTypeList[item.id]);
        this.clickTypeList[item.id] = null;
      }
    },
    // WMS点击事件
    clickWMS (wmsSource, item, wmsLayer) {
      const that = this
      //移除WMS点击事件
      unByKey(this.clickTypeList[item.id])
      //添加WMS点击事件
      this.clickTypeList[item.id] = this.map.on(
        'click',
        function (e) {
          if (that.activeToolType) {
            return;
          }
          //判断是否选中
          // 检查地图上是否有查询图层，如果有则只有查询图层可点击
          const hasQueryLayer = that.map.getLayers().getArray().some(layer =>
            layer.get('id') === 'query_in_query_drawer'
          );

          if (item.id === 'query_in_query_drawer' || (!hasQueryLayer && that.selectedItemId === item.id)) {
            const url = wmsSource.getFeatureInfoUrl(e.coordinate, that.map.getView().getResolution(), 'EPSG:4326', { INFO_FORMAT: 'application/json' })
            //判断是否有要素
            if (!that.map.hasFeatureAtPixel(e.coordinate)) {
              //请求要素
              fetch(url).then((response) => { return response.json() })
                .then((data) => {
                  console.log(data.features[0]);
                  if (data.features[0]) {
                    that.clickData(data.features[0]);
                    // 传递给 infoMap 组件
                    that.$emit('feature-selected', data.features[0]);
                  }
                })
            }
          }
        }
      )
    },
    // 点击要素
    clickData (item) {
      this.clearVectorLayer();
      // 清除之前的点击高亮（只保留最新的点击标记）
      this.clearClickHighlight();
      switch (item.geometry.type) {
        case 'LineString':
          this.clickLine(item);
          break;
        case 'Point':
          this.clickPoint(item);
          break;
      }
    },
    // 点击线要素
    clickLine (item) {
      const coordinates = item.geometry.coordinates;

      // 创建线要素
      const lineString = new LineString(coordinates);
      const lineFeature = new Feature({
        geometry: lineString,
        type: 'LineString'
      });

      // 添加到矢量图层
      this.vectorSource.addFeature(lineFeature);
    },
    // 点击点要素
    clickPoint (item) {
      // 创建点要素
      const point = new Point(item.geometry.coordinates);
      const pointFeature = new Feature({
        geometry: point,
        type: 'click-highlight-point'
      });

      // 添加到点击高亮图层（而不是矢量图层，这样不会被 clearVectorLayer 清除）
      if (this.clickHighlightSource) {
        this.clickHighlightSource.addFeature(pointFeature);
      }
    },
    //添加搜索点
    selectedPoint (longitude, latitude) {
      this.map.getView().setCenter([longitude, latitude]);
      this.map.getView().setZoom(18);

      // 清除之前的搜索标记
      this.clearSearchMarker();

      // 创建坐标图标
      const point = new Point([longitude, latitude]);
      const pointFeature = new Feature({
        geometry: point,
        type: 'Point',
        name: '搜索位置'
      });

      // 添加到搜索标记图层
      this.searchMarkerSource.addFeature(pointFeature);
    },

    // 地图定位（不添加标记点）
    setMapLocation (longitude, latitude, zoom = 18) {
      if (this.map) {
        const view = this.map.getView();
        view.setCenter([longitude, latitude]);
        view.setZoom(zoom);
      }
    },

    // 清除搜索标记
    clearSearchMarker () {
      if (this.searchMarkerSource) {
        this.searchMarkerSource.clear();
      }
    },

    // 打印
    printMap () {
      window.print()
    },
    /**
     * 导出地图为图片
     */
    exportMap () {
      try {
        console.log('开始导出地图图片...');

        this.map.once('rendercomplete', () => {
          const mapCanvas = document.createElement('canvas');
          const size = this.map.getSize();

          if (!size || size[0] === 0 || size[1] === 0) {
            console.error('地图尺寸无效:', size);
            return;
          }

          mapCanvas.width = size[0];
          mapCanvas.height = size[1];
          const mapContext = mapCanvas.getContext('2d');

          if (!mapContext) {
            console.error('无法获取Canvas上下文');
            return;
          }

          // 绘制所有图层到canvas
          const canvases = document.querySelectorAll('.ol-layer canvas');
          Array.prototype.forEach.call(canvases, (canvas) => {
            if (canvas.width > 0) {
              const opacity = canvas.parentNode.style.opacity || '1';
              mapContext.globalAlpha = opacity === '' ? 1 : Number(opacity);

              const transform = canvas.style.transform;
              if (transform && transform.includes('matrix')) {
                const matrixMatch = transform.match(/^matrix\(([^\(]*)\)$/);
                if (matrixMatch) {
                  const matrix = matrixMatch[1].split(',').map(Number);
                  if (matrix.length === 6) {
                    mapContext.setTransform(...matrix);
                  }
                }
              }

              mapContext.drawImage(canvas, 0, 0);
            }
          });

          // 生成文件名（包含时间戳）
          const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
          const fileName = `地图导出_${timestamp}.png`;

          // 保存图片
          mapCanvas.toBlob((blob) => {
            if (blob) {
              // 使用现代浏览器的下载方式
              const url = URL.createObjectURL(blob);
              const link = document.createElement('a');
              link.href = url;
              link.download = fileName;
              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);
              URL.revokeObjectURL(url);

              console.log('地图图片导出成功:', fileName);
              this.$message.success('地图图片导出成功');
            } else {
              console.error('无法生成图片文件');
              this.$message.error('导出失败，无法生成图片文件');
            }
          }, 'image/png', 0.95);
        });

        // 触发地图重新渲染
        this.map.renderSync();

      } catch (error) {
        console.error('导出地图图片失败:', error);
        this.$message.error('导出失败，请重试');
      }
    },
    /**
     * 导出地图为 ZIP 文件
     */
    exportDXF () {
      if (this.dxfExporting) {
        this.$message.warning('正在导出中，请勿重复点击');
        return;
      }
      this.dxfExporting = true;
      // duration: 0 表示不自动关闭，直到手动更新 key 为 export-dxf 的消息
      this.$message.loading({ content: '正在生成 ZIP 文件,导出时间较长，请耐心等待...', key: 'export-dxf', duration: 0 });

      this.$http.get('/pipe-network/gis_dwg/generate/gis', { responseType: 'blob' })
        .then(res => {
          // 因为 request.js 拦截器返回了 res.data，所以这里的 res 就是 blob 对象
          const blob = new Blob([res], { type: 'application/zip' });
          const fileName = `管网导出_${new Date().getTime()}.zip`;

          if (window.navigator.msSaveOrOpenBlob) {
            // 兼容 IE
            window.navigator.msSaveOrOpenBlob(blob, fileName);
          } else {
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = fileName;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
          }
          this.$message.success({ content: '导出成功', key: 'export-dxf' });
        })
        .catch(err => {
          console.error('导出 DXF 失败:', err);
          this.$message.error({ content: '导出失败，请联系管理员', key: 'export-dxf' });
        })
        .finally(() => {
          this.dxfExporting = false;
        });
    },

    /**
     * 缩放地图到指定比例尺
     * @param {Object} data - 缩放数据
     * @param {Number} data.ratio - 期望的比例尺显示值（单位：米），如100表示比例尺显示100米
     * @param {Object} data.item - 图层信息
     * @param {Number} data.index - 图层索引
     */
    scaleToRatio (data) {
      if (!this.map) {
        console.warn('地图实例不存在，无法执行缩放操作');
        return;
      }

      const { ratio } = data;
      console.log(`开始缩放地图，目标比例尺显示约 ${ratio} 米`);

      try {
        const view = this.map.getView();
        const currentCenter = view.getCenter();

        // 计算对应的分辨率
        // 对于 EPSG:4326 投影（经纬度坐标系）：
        // 1. 在赤道附近，1度约等于 111319.49米
        // 2. 比例尺控件通常显示的是地图上一定像素代表的实际距离
        // 3. 我们希望比例尺显示 ratio 米
        // 4. OpenLayers 比例尺控件的宽度通常是固定的（默认约100像素）
        // 5. 因此：resolution = (ratio米 / 比例尺控件像素宽度) / 每度对应的米数

        const metersPerDegree = 111319.49; // 在赤道附近的近似值
        const scaleBarWidth = 100; // 比例尺控件的近似像素宽度

        // 计算分辨率（度/像素）
        // ratio米 对应 scaleBarWidth像素
        // 所以 1像素 对应 ratio/scaleBarWidth 米
        const metersPerPixel = ratio / scaleBarWidth;
        const resolution = metersPerPixel / metersPerDegree;

        console.log('缩放参数:', {
          targetScaleDisplay: `${ratio}米`,
          metersPerPixel: metersPerPixel.toFixed(6),
          resolution: resolution.toFixed(10),
          currentCenter
        });

        // 使用动画效果平滑缩放
        view.animate({
          resolution: resolution,
          center: currentCenter,
          duration: 500 // 动画持续时间（毫秒）
        });

        console.log(`地图已缩放，比例尺应显示约 ${ratio} 米`);
      } catch (error) {
        console.error('缩放地图失败:', error);
        this.$message?.error('缩放地图失败');
      }
    },

    /**
     * 定位到管点并高亮显示（完整 GeoJSON）
     * @param {Object} geoData - GeoJSON 数据对象
     * @param {Object} info - 附加信息
     */
    locateToPoint (geoData, info = {}) {
      console.log('locateToPoint 被调用:', { geoData, info });

      if (!this.map) {
        console.warn('地图实例不存在，无法定位');
        this.$message.warning('地图尚未初始化，请稍后重试');
        return;
      }

      if (!geoData || !geoData.coordinates || geoData.type !== 'Point') {
        console.error('无效的 Point 数据:', geoData);
        this.$message.error('管点数据格式错误');
        return;
      }

      const coordinate = geoData.coordinates;
      console.log('提取坐标:', coordinate, '调用 locateToCoordinate');
      this.locateToCoordinate(coordinate[0], coordinate[1], 'point', info);
    },

    /**
     * 定位到指定坐标并高亮显示（兼容旧方法）
     * @param {Number} lng - 经度
     * @param {Number} lat - 纬度
     * @param {String} type - 类型：'point'（管点）或 'line'（管线）
     * @param {Object} info - 附加信息（包含完整的feature数据）
     */
    locateToCoordinate (lng, lat, type, info = {}) {
      if (!this.map) {
        console.warn('地图实例不存在，无法定位');
        return;
      }

      try {
        const view = this.map.getView();
        const coordinate = [lng, lat];

        console.log(`定位到${type === 'line' ? '管线' : '管点'}:`, { lng, lat, info });

        // 清除之前的定位高亮图层（使用独立方法，不影响其他矢量图层）
        this.clearLocationHighlight();

        // 创建独立的定位高亮图层（不会被 clearVectorLayer 清除）
        this.locationHighlightSource = new VectorSource();

        // 创建高亮点（简单的红色标记点，无动画）
        const highlightFeature = new Feature({
          geometry: new Point(coordinate),
          type: 'location-marker',
          name: info.name || info.classNo || '目标点'
        });

        this.locationHighlightSource.addFeature(highlightFeature);

        // 静态样式 - 简单的红色标记点，无动画
        this.locationHighlightLayer = new VectorLayer({
          source: this.locationHighlightSource,
          zIndex: 3000, // 提高 z-index，确保在最上层，不被其他图层覆盖
          style: new Style({
            image: new Circle({
              radius: 8,
              fill: new Fill({
                color: '#ff0000' // 纯红色
              }),
              stroke: new Stroke({
                color: '#ffffff',
                width: 2
              })
            }),
            text: new Text({
              text: info.name || info.classNo || '',
              font: 'bold 14px Arial',
              fill: new Fill({ color: '#ff0000' }),
              stroke: new Stroke({ color: '#ffffff', width: 3 }),
              offsetY: -20,
              backgroundFill: new Fill({ color: 'rgba(255, 255, 255, 0.9)' }),
              padding: [3, 6, 3, 6]
            })
          })
        });

        this.map.addLayer(this.locationHighlightLayer);
        console.log('定位高亮图层已添加到地图，zIndex:', 3000);

        // 停止之前可能存在的动画
        if (this.highlightAnimationId) {
          cancelAnimationFrame(this.highlightAnimationId);
          this.highlightAnimationId = null;
        }

        // 定位到该坐标，并设置适当的缩放级别（无动画）
        view.setCenter(coordinate);
        view.setZoom(18);

        // 显示提示信息
        // const tipText = type === 'line' 
        //   ? `已定位到管线: ${info.name || ''}`
        //   : `已定位到管点: ${info.name || info.classNo || ''}`;

        // this.$message.success(tipText);

        console.log('定位成功');
      } catch (error) {
        console.error('定位失败:', error);
        this.$message.error('定位失败');
      }
    },

    /**
     * 定位到管线并高亮显示（LineString）
     * @param {Object} geoData - GeoJSON 数据对象
     * @param {Object} info - 附加信息
     */
    locateToLineString (geoData, info = {}) {
      if (!this.map) {
        console.warn('地图实例不存在，无法定位');
        return;
      }

      if (!geoData || !geoData.coordinates || geoData.type !== 'LineString') {
        console.error('无效的 LineString 数据');
        this.$message.error('管线数据格式错误');
        return;
      }

      try {
        const view = this.map.getView();
        const coordinates = geoData.coordinates;

        console.log('定位到管线:', { coordinates, info });

        // 清除之前的定位高亮图层（使用独立方法，不影响其他矢量图层）
        this.clearLocationHighlight();

        // 计算管线的中心点（用于地图定位）
        let sumLng = 0;
        let sumLat = 0;
        coordinates.forEach(coord => {
          sumLng += coord[0];
          sumLat += coord[1];
        });
        const centerCoordinate = [sumLng / coordinates.length, sumLat / coordinates.length];

        // 创建独立的定位高亮图层（不会被 clearVectorLayer 清除）
        this.locationHighlightSource = new VectorSource();

        // 创建管线要素
        const lineFeature = new Feature({
          geometry: new LineString(coordinates),
          isLine: true,
          name: info.name || '管线'
        });

        // 在管线的起点和终点创建标记点
        const startFeature = new Feature({
          geometry: new Point(coordinates[0]),
          isPoint: true,
          pointType: 'start',
          name: '起点'
        });

        const endFeature = new Feature({
          geometry: new Point(coordinates[coordinates.length - 1]),
          isPoint: true,
          pointType: 'end',
          name: '终点'
        });

        // 在中心点创建文字标注
        const labelFeature = new Feature({
          geometry: new Point(centerCoordinate),
          isLabel: true,
          name: info.name || '管线'
        });

        this.locationHighlightSource.addFeature(lineFeature);
        this.locationHighlightSource.addFeature(startFeature);
        this.locationHighlightSource.addFeature(endFeature);
        this.locationHighlightSource.addFeature(labelFeature);

        // 动态样式函数 - 实现闪烁效果
        let animationFrame = 0;
        this.locationHighlightLayer = new VectorLayer({
          source: this.locationHighlightSource,
          zIndex: 3000, // 提高 z-index，确保在最上层
          style: (feature) => {
            if (feature.get('isLine')) {
              // 管线样式 - 闪烁效果
              const alpha = 0.6 + Math.sin(animationFrame * 0.1) * 0.3;
              return new Style({
                stroke: new Stroke({
                  color: `rgba(255, 0, 0, ${alpha})`,
                  width: 6
                })
              });
            } else if (feature.get('isPoint')) {
              // 起点/终点标记
              const pointType = feature.get('pointType');
              const isStart = pointType === 'start';
              const pulse = (animationFrame + (isStart ? 0 : 30)) % 60;
              const radius = 6 + Math.sin(pulse / 10) * 2;
              const alpha = 0.7 + Math.sin(animationFrame * 0.15) * 0.3;

              return new Style({
                image: new Circle({
                  radius: radius,
                  fill: new Fill({
                    color: isStart ? `rgba(0, 255, 0, ${alpha})` : `rgba(255, 165, 0, ${alpha})`
                  }),
                  stroke: new Stroke({
                    color: isStart ? '#00ff00' : '#ffa500',
                    width: 2
                  })
                }),
                text: new Text({
                  text: isStart ? '起' : '终',
                  font: 'bold 12px Arial',
                  fill: new Fill({ color: '#ffffff' })
                })
              });
            } else if (feature.get('isLabel')) {
              // 文字标注
              return new Style({
                text: new Text({
                  text: feature.get('name'),
                  font: 'bold 14px Arial',
                  fill: new Fill({ color: '#ff0000' }),
                  stroke: new Stroke({ color: '#ffffff', width: 3 }),
                  backgroundFill: new Fill({ color: 'rgba(255, 255, 255, 0.8)' }),
                  padding: [3, 6, 3, 6]
                })
              });
            }
          }
        });

        this.map.addLayer(this.locationHighlightLayer);
        console.log('管线定位高亮图层已添加到地图，zIndex:', 3000);

        // 动画循环
        const animate = () => {
          animationFrame++;
          this.locationHighlightLayer.changed();
          this.highlightAnimationId = requestAnimationFrame(animate);
        };

        // 停止之前的动画
        if (this.highlightAnimationId) {
          cancelAnimationFrame(this.highlightAnimationId);
        }

        animate();

        // 计算管线的边界范围
        const extent = lineFeature.getGeometry().getExtent();

        // 定位到管线，自动调整视图以显示整条管线
        view.fit(extent, {
          padding: [100, 100, 100, 100], // 周围留白
          duration: 1000, // 动画时长
          maxZoom: 18 // 最大缩放级别
        });

        console.log('管线定位动画已启动');

        // 5秒后停止动画（但保留标记）
        setTimeout(() => {
          if (this.highlightAnimationId) {
            cancelAnimationFrame(this.highlightAnimationId);
            this.highlightAnimationId = null;
          }
        }, 5000);

        // 显示提示信息
        this.$message.success(`已定位到管线: ${info.name || ''}`);

        console.log('管线定位成功');
      } catch (error) {
        console.error('管线定位失败:', error);
        this.$message.error('管线定位失败');
      }
    }

  }
};
</script>

<style lang="less">
// OpenLayers 比例尺全局样式（不 scoped，确保可以样式化动态创建的元素）
#scalebar .ol-scale-line {
  position: absolute !important;
  bottom: 55px !important;
  left: 0 !important;
  z-index: 1000 !important;
  backdrop-filter: blur(10px) !important;
  border-radius: 4px !important;
  padding: 8px 12px !important;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15) !important;
  border: 1px solid rgba(0, 0, 0, 0.1) !important;
}

#scalebar .ol-scale-line-inner {
  font-size: 12px !important;
  color: #333 !important;
  font-weight: 500 !important;
}
</style>

<style lang="less" scoped>
.map-container {
  width: 100%;
  height: 100%;
  position: relative;
  background: #fff;

  .map-content {
    width: 100%;
    height: 100%;
  }
}

// 确保地图图层不会产生闪烁效果
/deep/ .ol-viewport {
  .ol-layer {
    canvas {
      transition: none !important;
      filter: none !important;
    }
  }
}



// 光标样式
.map-content.cursor-measure-distance {
  cursor: crosshair !important;
}

.map-content.cursor-measure-surface {
  cursor: crosshair !important;
}

.map-content.cursor-analyze-query {
  cursor: crosshair !important;
}

.map-content.cursor-analyze-delete {
  cursor: crosshair !important;
}

// OpenLayers 比例尺样式 - 使用全局样式确保生效

// 移除可能导致闪烁的全局样式

// 测距功能提示样式
.measure-distance-tip {
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background: rgba(0, 0, 0, 0.8);
  color: white;
  padding: 12px 20px;
  border-radius: 8px;
  font-size: 14px;
  z-index: 1000;
  pointer-events: none;
  animation: fadeInOut 2s ease-in-out;
  white-space: nowrap;
  max-width: 90vw;
  text-align: center;
  box-sizing: border-box;
}

@keyframes fadeInOut {
  0% {
    opacity: 0;
    transform: translate(-50%, -50%) scale(0.8);
  }

  20% {
    opacity: 1;
    transform: translate(-50%, -50%) scale(1);
  }

  80% {
    opacity: 1;
    transform: translate(-50%, -50%) scale(1);
  }

  100% {
    opacity: 0;
    transform: translate(-50%, -50%) scale(0.8);
  }
}
</style>